import { Box, Stack } from "@mui/material";
import { useState } from "react";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import PageTabHeader from "@/App/Layouts/PageBody/PageTabHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AppIconButton from "@/common/components/Button/AppIconButton";
import Datetime from "@/common/components/Datetime/Datetime";
import AssetLink from "@/common/components/Entity/Asset/AssetLink";
import AssetSubscriptionPlanLink from "@/common/components/Entity/AssetSubscriptionPlan/AssetSubscriptionPlanLink";
import ContractLink from "@/common/components/Entity/Contract/ContractLink";
import CustomerLink from "@/common/components/Entity/Customer/CustomerLink";
import SubscriptionPlanDurationInline from "@/common/components/Entity/Subscription/common/SubscriptionPlanDurationInline";
import SubscriptionPlanExtraOptionInline from "@/common/components/Entity/Subscription/common/SubscriptionPlanExtraOptionInline";
import SubscriptionPlanPriceInline from "@/common/components/Entity/Subscription/common/SubscriptionPlanPriceInline";
import EntityChangedByInfoCard from "@/common/components/EntityInfo/EntityChangedByInfoCard";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import FieldValue from "@/common/components/Form/Display/FieldValue";
import AllocationStatusIcon from "@/common/components/Icons/AllocationStatusIcon";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppTooltip from "@/common/components/Tooltip/AppTooltip";
import { ViewVariant } from "@/common/ts/layout";
import { AppPermission, AssetSubscriptionDto, EntityType } from "@/core/api/generated/v1";

import EntityAffiliationInfoBlock from "../../../EntityAffiliation/EntityAffiliationInfoBlock";
import AssetSubscriptionReallocateAssetModal from "../../AssetSubscriptionReallocateAssetModal";
import AssetSubscriptionSpotInfoDisplay from "../../AssetSubscriptionSpotInfoDisplay";
import AssetSubscriptionStatusDisplayAndInput from "../../AssetSubscriptionStatusDisplayAndInput";

interface Props {
  assetSubscription: AssetSubscriptionDto;
  onUpdate: (newValue?: AssetSubscriptionDto) => void;
}

export default function OverviewTabContent({ assetSubscription, onUpdate }: Props) {
  const [isReallocateAssetModalOpen, setIsReallocateAssetModalOpen] = useState(false);

  return (
    <PageTabContent>
      <ViewLayout
        displayProps={{ viewVariant: ViewVariant.Tab }}
        header={
          <PageTabHeader
            title={"Details"}
            subtitle2={<EntityChangedByInfoCard entity={assetSubscription} />}
          />
        }
      >
        <ViewContentBlock>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: `1fr`,
                md: `1fr`,
                lg: `1fr`,
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 4,
            }}
          >
            <Box>
              <Stack spacing={1}>
                <FieldValue label='Subscription plan' isEmpty={!assetSubscription?.plan}>
                  <AssetSubscriptionPlanLink entity={assetSubscription?.plan} />
                </FieldValue>

                <FieldValue label='Customer' isEmpty={!assetSubscription?.customer}>
                  <CustomerLink entity={assetSubscription?.customer} />
                </FieldValue>

                <FieldValue label='Contract' isEmpty={!assetSubscription?.contract}>
                  <ContractLink entity={undefined} entityId={assetSubscription?.contract?.id} />
                </FieldValue>

                <FieldValue label='Asset' isEmpty={!assetSubscription?.asset}>
                  <Stack>
                    <Box>
                      {assetSubscription.asset?.asset && (
                        <AssetLink entity={assetSubscription.asset?.asset} />
                      )}

                      {!assetSubscription.asset?.asset && (
                        <InlineApiEnumValue
                          type='AssetEntityType'
                          value={assetSubscription.asset?.entityType}
                        />
                      )}
                    </Box>

                    <Box>
                      <AllocationStatusIcon status={assetSubscription.allocationStatus} />{" "}
                      <InlineApiEnumValue
                        type='AllocationStatus'
                        value={assetSubscription.allocationStatus}
                        withHelperTooltip
                      />{" "}
                      {assetSubscription?.canReallocate && (
                        <AppTooltip title='Reallocate asset' isInline>
                          <AppIconButton
                            size='small'
                            onClick={() => setIsReallocateAssetModalOpen(true)}
                          >
                            <AppIcon of='edit' />
                          </AppIconButton>
                        </AppTooltip>
                      )}
                    </Box>
                  </Stack>
                </FieldValue>

                <FieldValue label='Base price' isEmpty={!assetSubscription?.basePrice}>
                  <SubscriptionPlanPriceInline
                    basePrice={assetSubscription.basePrice}
                    price={assetSubscription.basePrice}
                    currency={assetSubscription.currency}
                  />
                </FieldValue>
                <FieldValue label='Price' isEmpty={!assetSubscription?.price}>
                  <SubscriptionPlanPriceInline
                    basePrice={assetSubscription.basePrice}
                    price={assetSubscription.price}
                    currency={assetSubscription.currency}
                  />
                </FieldValue>
                <FieldValue label='Duration' isEmpty={!assetSubscription?.duration}>
                  <SubscriptionPlanDurationInline
                    duration={assetSubscription.duration}
                    currency={assetSubscription.currency}
                  />
                </FieldValue>
                <FieldValue label='Extra options' isEmpty={!assetSubscription?.extraOptions}>
                  <Stack spacing={0.5}>
                    {assetSubscription?.extraOptions?.map((x, i) => (
                      <SubscriptionPlanExtraOptionInline
                        key={i}
                        extraOption={x}
                        currency={assetSubscription.currency}
                      />
                    ))}
                  </Stack>
                </FieldValue>
              </Stack>
            </Box>

            <Box>
              <Stack spacing={1}>
                <FieldValue label='Local number' isEmpty={!assetSubscription.localNumber}>
                  {assetSubscription.localNumber}
                </FieldValue>

                <FieldValue label='External number' isEmpty={!assetSubscription.externalNumber}>
                  {assetSubscription.externalNumber}
                </FieldValue>

                <FieldValue label='Status' isEmpty={!assetSubscription?.status}>
                  <AssetSubscriptionStatusDisplayAndInput
                    assetSubscription={assetSubscription}
                    onUpdated={(newValue) => {
                      onUpdate && onUpdate(newValue);
                    }}
                  />
                </FieldValue>

                <FieldValue
                  label='Starts at'
                  direction='row'
                  isEmpty={!assetSubscription?.startsAt}
                >
                  <Datetime datetime={assetSubscription.startsAt} withDurationFromNow />
                </FieldValue>

                <FieldValue label='Ends at' direction='row' isEmpty={!assetSubscription?.endsAt}>
                  <Datetime datetime={assetSubscription.endsAt} withDurationFromNow />
                </FieldValue>
              </Stack>
            </Box>
          </Box>
        </ViewContentBlock>

        {/* Affiliation */}
        <EntityAffiliationInfoBlock
          entityType={EntityType.AssetSubscription}
          entityId={assetSubscription.id}
          entity={assetSubscription}
          entityUpdatePermission={AppPermission.AssetSubscriptionManage}
          infoDisplayProps={{
            withoutTenant: true,
          }}
          onUpdate={onUpdate}
        />

        {/* Check in, check out spot info */}
        <ViewContentBlock>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: `1fr`,
                lg: `repeat(2, 1fr)`,
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 4,
            }}
          >
            <AssetSubscriptionSpotInfoDisplay
              title='Check out spot info'
              info={assetSubscription?.checkOutSpotInfo}
            />

            <AssetSubscriptionSpotInfoDisplay
              title='Check in spot info'
              info={assetSubscription?.checkInSpotInfo}
            />
          </Box>
        </ViewContentBlock>

        {/* Reallocate asset */}
        {assetSubscription && (
          <AssetSubscriptionReallocateAssetModal
            open={isReallocateAssetModalOpen}
            onClose={() => setIsReallocateAssetModalOpen(false)}
            updateProps={{
              assetSubscription: assetSubscription,
              onSave: (newValue) => {
                onUpdate(newValue);
                setIsReallocateAssetModalOpen(false);
              },
            }}
          />
        )}
      </ViewLayout>
    </PageTabContent>
  );
}
